import { t } from '@lingui/macro';
import { TSingleApplication } from 'lendingApi/applications.types';
import { TApplicationStatus, TStepType } from 'types';

const getNumberOfOfferColumns = ({
  formal_offer_sets,
  requested_products,
}: TSingleApplication) => {
  const formalOffers = formal_offer_sets?.[0]?.formal_offers;

  if (formalOffers?.length > 0) {
    if (formalOffers?.find((item) => item.is_selected)) {
      return 1;
    } else {
      return formalOffers.length;
    }
  }
  return requested_products.length;
};

/**
 * APPLICATIONS
 */
export const APPLICATION_STATUS: Record<
  TSingleApplication['status'],
  TSingleApplication['status']
> = {
  funded: 'funded',
  expired: 'expired',
  talking_to_customer: 'talking_to_customer',
  offered: 'offered',
  declined: 'declined',
  deferred: 'deferred',
  in_underwriting: 'in_underwriting',
  awaiting_underwriter: 'awaiting_underwriter',
  open: 'open',
  action_required: 'action_required',
  offer_revoked: 'offer_revoked',
  customer_to_link_open_banking: 'customer_to_link_open_banking',
  conditional_offer: 'conditional_offer',
  offer_rejected: 'offer_rejected',
  conditional_offer_criteria_met: 'conditional_offer_criteria_met',
  manual_review: 'manual_review',
} as const;

// USING FUNCTION HERE TO MAKE LINGUI WORK
export const APPLICATION_STEPS = () => [
  {
    step: 0,
    label: t`App created`,
    statuses: [APPLICATION_STATUS.open],
  },
  {
    step: 1,
    label: t`Docs required`,
    statuses: [
      APPLICATION_STATUS.manual_review,
      APPLICATION_STATUS.action_required,
    ],
  },
  {
    step: 2,
    label: t`App in review`,
    statuses: [
      APPLICATION_STATUS.awaiting_underwriter,
      APPLICATION_STATUS.in_underwriting,
      APPLICATION_STATUS.customer_to_link_open_banking,
    ],
  },
  {
    step: 3,
    label: t`App decision`,
    statuses: [
      // Declined
      APPLICATION_STATUS.deferred,
      APPLICATION_STATUS.declined,
      APPLICATION_STATUS.offer_rejected,

      //   Approved
      APPLICATION_STATUS.offered,
      APPLICATION_STATUS.conditional_offer,
    ],
  },
  {
    step: 4,
    label: t`Funded`,
    statuses: [APPLICATION_STATUS.talking_to_customer],
  },
  {
    step: 5,
    label: t`Complete`,
    statuses: [APPLICATION_STATUS.funded],
  },
];

export const APPLICATION_PAGE_STEP = (
  application: TSingleApplication,
  rejectReason: string,
): TStepType => {
  const { formal_offer_sets, status, customer } = application;
  const offers = formal_offer_sets[0]?.formal_offers;
  const amountOfOffers = offers?.length || 0;
  const multipleOffers =
    amountOfOffers > 1 && !Boolean(offers?.find((item) => item.is_selected));

  switch (status) {
    case APPLICATION_STATUS.open: {
      if (customer.date_sign_up_finished) {
        return {
          heading: t`Documents required`,
          subHeading: t`To complete this application, please upload the required documents and any additional supporting information in the documents tab.`,
          button: true,
        };
      } else {
        return {
          heading: t`Continue the application`,
          subHeading: t`Pick up where you left off.`,
          button: true,
        };
      }
    }
    case APPLICATION_STATUS.action_required: {
      return {
        heading: t`Documents required`,
        subHeading: t`To complete this application, please upload the required documents and any additional supporting information in the documents tab.`,
      };
    }
    case APPLICATION_STATUS.customer_to_link_open_banking: {
      return {
        heading: t`Link open banking`,
        subHeading: t`The application is with your client to link their trading bank account. Notify your client to link their trading account with open banking.`,
      };
    }
    case APPLICATION_STATUS.offer_revoked: {
      return {
        heading: amountOfOffers > 1 ? t`Offers revoked` : t`Offer revoked`,
        subHeading:
          amountOfOffers > 1
            ? t`Unfortunately, the offers for this application have been revoked.`
            : t`Unfortunately, the offer for this application has been revoked.`,
      };
    }
    case APPLICATION_STATUS.manual_review: {
      return {
        heading: t`Manual check`,
        subHeading: t`Our team needs to manually check details in the application before it can be reviewed.`,
      };
    }
    case APPLICATION_STATUS.awaiting_underwriter: {
      return {
        heading: t`Awaiting underwriter`,
        subHeading: t`The application is complete and an underwriter will be assigned to review it soon.`,
      };
    }
    case APPLICATION_STATUS.in_underwriting: {
      return {
        heading: t`In review by underwriter`,
        subHeading: t`An underwriter is reviewing the application - we’ll send you an update about it soon.`,
      };
    }
    case APPLICATION_STATUS.declined: {
      return {
        heading: t`Application declined`,
        subHeading: t`The application has been declined. Where possible, we’ll send details to you by email on why we’ve made this decision.`,
      };
    }
    case APPLICATION_STATUS.deferred: {
      return {
        heading: t`Application deferred`,
        subHeading: t`We’re currently not able to proceed with this application.`,
      };
    }
    case APPLICATION_STATUS.expired: {
      return {
        heading: t`Refresh`,
        subHeading: multipleOffers
          ? t`The original offers have expired, but you can resubmit the original application to review the applicant for an updated decision. Additional documents may be required.`
          : t`The original offer has expired, but you can resubmit the original application to review the applicant for an updated decision. Additional documents may be required.`,
      };
    }
    case APPLICATION_STATUS.conditional_offer: {
      return {
        heading:
          amountOfOffers > 1
            ? 'Review conditional offers'
            : 'Review conditional offer',
        subHeading:
          amountOfOffers > 1
            ? "These offers depend on reviewing the applicant's latest VAT return(s), and they may be withdrawn based on that information."
            : "This offer depends on reviewing the applicant's latest VAT return(s), and it may be withdrawn based on that information.",
      };
    }
    case APPLICATION_STATUS.offered: {
      const numberOfColumns = getNumberOfOfferColumns(application);

      if (numberOfColumns > 1) {
        return {
          heading:
            numberOfColumns === 2
              ? t`Congratulations, you have two approved offers!`
              : t`Congratulations, you have multiple approved offers!`,
          subHeading: t`Select one to see the early repayment plan below.`,
        };
      } else {
        return {
          heading: t`Congratulations, this is your approved offer!`,
          subHeading: t`You can see the early repayment plan below.`,
          link: t`Go to review offer tab`,
        };
      }
    }
    case APPLICATION_STATUS.offer_rejected: {
      return {
        heading: amountOfOffers > 1 ? t`Offers rejected` : t`Offer rejected`,
        subHeading: rejectReason,
      };
    }
    case APPLICATION_STATUS.talking_to_customer: {
      return {
        heading: t`Applicant funding steps`,
        subHeading: t`The applicant has accepted an offer and just needs to complete the following funding steps to access funds.`,
      };
    }
    case APPLICATION_STATUS.funded: {
      return {
        heading: t`Offer funded`,
        subHeading: t`Congratulations, the applicant has taken their funds.`,
      };
    }
    default:
      return {} as TStepType;
  }
};

/**
 * PRODUCTS
 */
export const PRODUCT_NAMES = {
  business_loan: 'Term Loan',
  term_loan: 'Term Loan',
  credit_facility: 'Flexi-Loan',
  cbils: 'CBILS',
  iwocapay: 'iwocaPay',
  flexi24: 'Flexi-24',
  recovery_loan: 'Recovery Loan',
  merchant_cash_advance: 'Revenue Based Loan',
};

export const PRODUCT_DISPLAY_ORDER = [
  PRODUCT_NAMES.credit_facility,
  PRODUCT_NAMES.flexi24,
  PRODUCT_NAMES.term_loan,
];

/**
 * APPLICATION TABLE
 */
export const APPLICATION_TABLE_KEYS = {
  created: 'created',
  customer_name: 'customer_name',
  status: 'status',
  product_type: 'product_type',
  term_length: 'term_length',
  expiry: 'expiry',
  company_name: 'company_name',
  approval_amount: 'approval_amount',
  requested_amount: 'requested_amount',
  referred_by: 'referred_by',
} as const;

export const APPLICATION_TABLE_KEY_NAMES = {
  created: 'Created',
  customer_name: 'Applicant',
  status: 'Application status',
  product_type: 'Product',
  term_length: 'Term length',
  expiry: 'Expiry date',
  company_name: 'Company name',
  approval_amount: 'Approval amount',
  requested_amount: 'Requested amount',
  referred_by: 'Referred by',
};

export const PRODUCT_TYPE_FILTERS = (includeTermLoan: boolean) => {
  const filters = [
    { value: '-', label: '-' },
    { value: PRODUCT_NAMES.credit_facility, label: t`Flexi-Loan` },
    { value: PRODUCT_NAMES.flexi24, label: t`Flexi-24` },
  ];

  if (includeTermLoan) {
    filters.push({
      value: PRODUCT_NAMES.term_loan,
      label: PRODUCT_NAMES.term_loan,
    });
  }
  return filters;
};

export const TERM_LENGTH_FILTERS = (includeTermLoan: boolean) => {
  const filters = [
    { value: '-', label: '-' },
    { value: '12 months', label: t`12 months` },
    { value: '24 months', label: t`24 months` },
  ];

  if (includeTermLoan) {
    filters.push({
      value: '60 months',
      label: '60 months',
    });
  }
  return filters;
};

// USING A FUNCTION HERE BECAUSE LINGUI DOESN'T WORK WITHOUT IT
export const APPLICATION_STATUS_FILTERS = () => [
  {
    group: t`Ongoing app`,
    items: [
      { value: APPLICATION_STATUS.open, label: t`Continue app` },
      { value: APPLICATION_STATUS.action_required, label: t`Docs required` },
      { value: APPLICATION_STATUS.manual_review, label: t`Manual check` },
    ],
  },
  {
    group: t`App in review`,
    items: [
      {
        value: APPLICATION_STATUS.awaiting_underwriter,
        label: t`Awaiting underwriter`,
      },
      {
        value: APPLICATION_STATUS.in_underwriting,
        label: t`In review by underwriter`,
      },
    ],
  },
  {
    group: t`App decision`,
    items: [
      { value: APPLICATION_STATUS.offered, label: t`Review offer(s)` },
      {
        value: APPLICATION_STATUS.conditional_offer,
        label: t`Review conditional offer(s)`,
      },
      { value: APPLICATION_STATUS.declined, label: t`App declined` },
      { value: APPLICATION_STATUS.deferred, label: t`App deferred` },
      { value: APPLICATION_STATUS.offer_rejected, label: t`Offer rejected` },
      { value: APPLICATION_STATUS.expired, label: t`Offer expired` },
    ],
  },
  {
    group: t`Funding`,
    items: [
      {
        value: APPLICATION_STATUS.talking_to_customer,
        label: t`Applicant funding steps`,
      },
      { value: APPLICATION_STATUS.funded, label: t`Funded` },
    ],
  },
];

export const ALL_STATUS_VALUES = [...Object.values(APPLICATION_STATUS)];

export const PENDING_STATUS_VALUES = [
  APPLICATION_STATUS.open,
  APPLICATION_STATUS.action_required,
  APPLICATION_STATUS.expired,
];

export const OFFERED_STATUS_VALUES = [
  APPLICATION_STATUS.offered,
  APPLICATION_STATUS.conditional_offer,
];

export const EXPIRING_STATUS_VALUES: (
  | TSingleApplication['status']
  | 'isExpiring'
)[] = [
  APPLICATION_STATUS.offered,
  APPLICATION_STATUS.conditional_offer,
  APPLICATION_STATUS.talking_to_customer,
  'isExpiring',
];

export const APPLICATION_STATUS_LABEL = (
  status: keyof typeof APPLICATION_STATUS,
): string => {
  for (const filter of APPLICATION_STATUS_FILTERS()) {
    for (const item of filter.items) {
      if (item.value === status) {
        return item.label;
      }
    }
  }
  return '';
};

export const APPLICATION_STATUS_VALUES = () =>
  ({
    declined: t`App declined`,
    deferred: t`App deferred`,
    offered: t`Review offer`,
    conditional_offer: t`Conditional offer`,
    open: t`Continue app`,
    funded: t`Funded`,
    expired: t`Offer expired`,
    talking_to_customer: t`Applicant funding steps`,
    in_underwriting: t`In review by underwriter`,
    manual_review: t`Manual check`,
    awaiting_underwriter: t`Awaiting underwriter`,
    action_required: t`Docs required`,
    offer_revoked: t`Offer revoked`,
    customer_to_link_open_banking: t`Client to link bank account`,
    offer_rejected: t`Offer rejected`,
  }) as const;

export const APPLICATION_STATUS_VALUE = (status: TApplicationStatus) => {
  if (APPLICATION_STATUS_VALUES()[status]) {
    return APPLICATION_STATUS_VALUES()[status];
  }
  return t`Ongoing`;
};
